import {
  AfterViewInit,
  Component, createNgModule,
  HostListener,
  Injector,
  NgZone,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { UserService } from './services/user/user.service';
import { filter, Subscription, take } from 'rxjs';
import { DoorService } from './services/door/door.service';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { ScenarioImageService } from './services/scenario-image/scenario-image.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { isMobileWebBrowser } from './utils/device.utils';
import { CustomerService } from './services/customer/customer.service';
import {FilterService, GenericFilter} from './services/filter/filter.service';
import { UserStorageService } from './services/user/user.storage.service';
import { FavoriteDoorService } from './services/favorite-door/favorite-door.service';
import { ProductsMap } from './services/door/door.service.impl';
import { AppView } from './models/app.view';
import {FaviconService} from "./services/favicon-service";
import {CustomerServiceImpl} from "./services/customer/customer.service.impl";

type AppState = 'DESKTOP' | 'MOBILE' | undefined

@Component({
  selector: 'app-main',
  template: '<ng-template #content></ng-template>',
})
export class MainComponent implements OnInit, AfterViewInit {

  public isUserCreated: boolean = false;

  constructor(
    private customerService: CustomerService,
    private userService: UserService,
    private userStorageService: UserStorageService,
    private scenarioImageService: ScenarioImageService,
    private favoriteDoorService: FavoriteDoorService,
    private doorService: DoorService,
    private filterService: FilterService,
    private route: ActivatedRoute,
    private faviconService: FaviconService,
    private router: Router,
    private modalService: NgbModal,
    private injector: Injector,
    private zone: NgZone) {

    if (Capacitor.isNativePlatform()) {
      App.addListener('appUrlOpen', data => {
        this.zone.run(() => {
          const paramsSplit = data.url.split('?');
          if (paramsSplit.length == 2) {
            this.router.navigateByUrl('/?' + paramsSplit[1]);
          }
        });
      });
    }
  }

  @ViewChild('content', {read: ViewContainerRef})
  template: ViewContainerRef | undefined;

  lastState: AppState = undefined;
  subs: Subscription[] = [];

  async ngOnInit() {
    await this.lockScreenOrientation();
    this.customerService.loadCustomer();

    const customerKey = CustomerServiceImpl.GetCustomerKey()
    this.faviconService.setFaviconsForCustomer(customerKey);
  }

  async ngAfterViewInit(): Promise<void> {
    this.subToQueryParameters();
    await this.onResize();
  }

  @HostListener('window:resize', ['$event'])
  async onResize(event?: Event | undefined): Promise<void> {

    if (window.innerWidth <= 768 && this.lastState !== 'MOBILE') {
      this.lastState = 'MOBILE';

      // close any modal before switching
      this.modalService.dismissAll();

      if (this.route.snapshot.data['viewMode'] === AppView.App) {
        const {MobileAppModule} = await import('./mobile/mobile.app.module');
        this.template!.clear();
        this.template!.createComponent(MobileAppModule.components.MobileAppComponent, {
          ngModuleRef: createNgModule(MobileAppModule, this.injector)
        });
      } else {
        const {MobileAppModule} = await import('./widget/mobile/mobile.app.module');
        this.template!.clear();
        this.template!.createComponent(MobileAppModule.components.MobileAppWidgetComponent, {
          ngModuleRef: createNgModule(MobileAppModule, this.injector)
        });
      }

      this.scenarioImageService.setOrientationFilter('VERTICAL');

      event?.preventDefault();
      event?.stopImmediatePropagation();
    } else if (window.innerWidth > 768 && this.lastState !== 'DESKTOP' && !Capacitor.isNativePlatform() && !isMobileWebBrowser()) {
      this.lastState = 'DESKTOP';

      // close any modal before switching
      this.modalService.dismissAll();

      if (this.route.snapshot.data['viewMode'] === AppView.App) {
        const {DesktopAppModule} = await import('./desktop/desktop.app.module');
        this.template!.clear();
        this.template!.createComponent(DesktopAppModule.components.DesktopAppComponent, {
          ngModuleRef: createNgModule(DesktopAppModule, this.injector)
        });
      } else {
        const {DesktopWidgetModule} = await import('./widget/desktop/desktop.app.module');
        this.template!.clear();
        this.template!.createComponent(DesktopWidgetModule.components.DesktopWidgetComponent, {
          ngModuleRef: createNgModule(DesktopWidgetModule, this.injector)
        });
      }

      this.scenarioImageService.setOrientationFilter('HORIZONTAL');

      event?.preventDefault();
      event?.stopImmediatePropagation();
    }
  }

  private async lockScreenOrientation(): Promise<void> {
    try {
      if (window.screen?.orientation) {
        await (window.screen.orientation as any).lock('portrait');
      }
    } catch (e) {
    }
  }

  private subToQueryParameters(): void {
    let doorsSub: Subscription | undefined = undefined;
    let scenarioSub: Subscription | undefined = undefined;
    this.subs.push(this.route.queryParamMap.subscribe(async queryParams => {
      // Reroute 'door' to d
      if (queryParams.has('door')) {
        scenarioSub?.unsubscribe();

        await this.router.navigate(
          [],
          {
            relativeTo: this.route,
            queryParams: {
              d: queryParams.get('door'),
              door: null,
            },
            queryParamsHandling: 'merge'
          })
        return;
      }

      // Set example image set number
      if (queryParams.has('set')) {
        try {
          const setNumber = parseInt(queryParams.get('set')!);
          this.scenarioImageService.setExampleImageSetNumber(setNumber);
        } catch (e) {
          console.warn('set parameter is not a number', e);
        }
      }

      // Handle custom attributes starting with 'ca_<name>'
      // @ts-ignore
      const customAttributes = Object.keys(queryParams.params).filter(key => key.startsWith('ca_'));
      if (customAttributes.length !== 0) {
        const customAttributesValues = customAttributes.map(key => {

          // Split values by comma if there
          const valuesSplit =  queryParams.getAll(key).map(value => value.split(','));
          const valuesFlat = valuesSplit.flat();

          return {
            attributeName: key.substring(3),
            values: valuesFlat,
          }
        });

        this.filterService.setFilter({
          customAttributes: customAttributesValues
        });
      }

      if (!this.subToSelectedDoor) {
        this.subToSelectedDoor = this.doorService.getSelected$().subscribe(productsMap => {
          this.router.navigate(
            [],
            {
              relativeTo: this.route,
              queryParams: MainComponent.getUrlParamsFromProducts(productsMap),
              queryParamsHandling: 'merge'
            });
        });
        this.subs.push(this.subToSelectedDoor);
      }

      if (!this.subToFilter) {
        this.subToFilter = this.filterService.getFilter$().subscribe(filter => {
          const newQueryParams = this.getUrlParamsFromFilter(filter);

          if (newQueryParams) {
            this.router.navigate(
              [],
              {
                relativeTo: this.route,
                queryParams: this.getUrlParamsFromFilter(filter) || null,
                queryParamsHandling: 'merge'
              });
          } else {
            const queryParams = this.route.snapshot.queryParamMap;

            // Remove all ca_ parameters
            const paramsToRemove = queryParams.keys.filter(key => key.startsWith('ca_'));
            this.router.navigate(
              [],
              {
                relativeTo: this.route,
                queryParams: paramsToRemove.map(key => ({[key]: null})).reduce((acc, val) => ({...acc, ...val}), {}),
                queryParamsHandling: 'merge'
              })
          }
        });
        this.subs.push(this.subToFilter);
      }

      if (queryParams.has('u')) {
        this.userStorageService.storeUserId(queryParams.get('u')!);

        await this.router.navigate(
          [],
          {
            relativeTo: this.route,
            queryParams: {
              u: undefined
            },
            queryParamsHandling: 'merge'
          });
      }

      // create user
      if (!this.isUserCreated) {
        await this.userService.createUserIfNotExists();
        this.isUserCreated = true;
        await this.favoriteDoorService.loadFavoriteDoorCount();
      }

      const productsFromUrlParams = this.getProductsFromUrlParams(queryParams)
      await this.doorService.setSelectedProductsByExternalIds(productsFromUrlParams)

      const scenario = this.scenarioImageService.getSelected()
      this.scenarioImageService.getSelected$().subscribe(selected => {
        for (const corner of selected.corners) {
          const currentDoorAtIndex = this.doorService.getSelectedDoorAtIndex(corner.index);
          if (!currentDoorAtIndex) {
            doorsSub = this.doorService.getProductsObservable().pipe(
              filter(products => products.length !== 0),
              take(1)
            ).subscribe({
              next: products => {
                this.doorService.selectProductAtIndex({ product: products[0], index: corner.index });
              }
            });
          }
        }
      })

      const currentDoor = this.doorService.getSelectedDoorAtCurrentIndex();
      if (currentDoor === undefined) {
        // set referral door to undefined to indicate "direct traffic"
        this.filterService.setReferralDoor(undefined);

        doorsSub = this.doorService.getProductsObservable().pipe(
          filter(products => products.length !== 0),
          take(1)
        ).subscribe({
          next: products => {
            this.doorService.selectProductAtCurrentIndex(products[0]);
          }
        });
      } else {
        // set referral door for filter
        this.filterService.setReferralDoor(currentDoor);
      }

      // handle scenarioId
      if (queryParams.has('s')) {
        scenarioSub?.unsubscribe();
        try {
          const scenarioId = queryParams.get('s') as string;
          const parts = scenarioId.split('_');
          if (parts.length == 2) {
            const showedSuccessful = await this.scenarioImageService.selectScenarioByShortId(parts[1], parts[0]);

            if (!showedSuccessful) {
              console.warn('no scenario with shortId ' + queryParams.get('s') + ' was found. Trying the second scenario if present');
            }

            // If the scenario was not showed successfully, we can check if a second example image was passed in
            if (!showedSuccessful && queryParams.has('s2')) {
              const scearioId2 = queryParams.get('s2') as string;
              const parts2 = scearioId2.split('_');
              if (parts2.length == 2) {
                const showedSecond = await this.scenarioImageService.selectScenarioByShortId(parts2[1], parts2[0]);
              }
            }
          }
        } catch (e) {
          console.warn('no scenario with shortId ' + queryParams.get('s') + ' was found');
        }
      }

      const currentScenario = this.scenarioImageService.getSelected();
      if (currentScenario === undefined) {
        scenarioSub = this.scenarioImageService.getScenarios$().pipe(
          filter(scenarios => scenarios.length !== 0),
          take(1)
        ).subscribe({
          next: scenarios => {
            this.scenarioImageService.selectScenario(scenarios[0]);
          }
        });
      }
    }));

    this.subs.push(this.scenarioImageService.getSelected$().subscribe(selected => {
      this.router.navigate(
        [],
        {
          relativeTo: this.route,
          queryParams: {
            s: selected.type + '_' + selected.shortId,
            s2: undefined
          },
          queryParamsHandling: 'merge'
        });
    }));
  }

  private subToSelectedDoor: Subscription | undefined = undefined;
  private subToFilter: Subscription | undefined = undefined;

  private getProductsFromUrlParams(queryParams: ParamMap): Array<{ index: number, door?: string, products?: Array<string> }> {
    const products: Array<{ index: number, door?: string, products?: Array<string> }> = []

    const getProductsForIndex = (index: number): Array<string> => {
      const products: Array<string> = []
      for (let i = 1; i < 10; i++) {
        const param = `p${index}-${i}`;
        if (queryParams.has(param)) {
          products.push(queryParams.get(param) as string);
        }
      }
      return products
    }

    for (let i = 1; i < 10; i++) {
      const doorParam = i === 1 ? 'd' : `d${i}`
      const door = queryParams.get(doorParam) as string | undefined
      const productsForIndex = getProductsForIndex(i)

      if ((door !== null && door !== undefined) || productsForIndex.length !== 0) {
        products.push({
          index: i,
          door,
          products: productsForIndex
        })
      }
    }

    return products
  }

  private getUrlParamsFromFilter(filter: GenericFilter | undefined): Params | undefined {
    if (!filter || !filter.customAttributes) {
      return undefined;
    }

    let queryParams: Params = {};
    if (filter.customAttributes) {
      filter.customAttributes.forEach((customAttribute, index) => {
        const paramName = `ca_${customAttribute.attributeName}`;
        const paramValue = customAttribute.values.join(',');
        queryParams[paramName] = paramValue;
      });
    }

    return queryParams;
  }

  public static getUrlParamsFromProducts(productsMap: ProductsMap): Params {
    let queryParams: Params = {};

    productsMap.forEach((productsAtIndex, index) => {

      const door = productsAtIndex.door

      if (door) {
        const paramName = productsAtIndex.index === 1 ? 'd' : `d${productsAtIndex.index}`;
        const paramValue = door.externalIds[0];
        queryParams[paramName] = paramValue;
      }

      productsAtIndex.products?.forEach((product, productIndex) => {
        if (product.productType === 'door') {
          return;
        }

        if (product && product.externalIds && product.externalIds.length !== 0) {
          const paramName = `p${index}-${productIndex+1}`;
          const paramValue = product.externalIds[0];
          queryParams[paramName] = paramValue;

          productIndex++;
        }
      });
    });

    return queryParams;
  }
}
